import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Play,
  Calendar,
  Eye,
  Loader2,
  Pencil,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Settings,
  FileText,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CreateScheduleDialog } from './CreateScheduleDialog';
import { CopyAsOptimizableDialog } from './CopyAsOptimizableDialog';
import { EvalWizard } from './EvalWizard';
import {
  UseEvalDetailReturn,
  EvalRun,
  Variant,
} from './hooks/useEvalDetail';

interface StandardEvalDetailProps {
  evalId: string;
  hook: UseEvalDetailReturn;
}

function StatusBadge({ status }: { status: string }) {
  const icons: Record<string, React.ReactNode> = {
    completed: <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />,
    completed_with_errors: <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />,
    running: <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />,
    pending: <Clock className="h-3.5 w-3.5 text-muted-foreground" />,
    failed: <XCircle className="h-3.5 w-3.5 text-red-500" />,
  };

  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    completed: 'default',
    completed_with_errors: 'secondary',
    running: 'secondary',
    pending: 'outline',
    failed: 'destructive',
    cancelled: 'outline',
  };

  const displayName = status === 'completed_with_errors' ? 'partial' : status;

  return (
    <Badge variant={variants[status] || 'outline'} className="flex items-center gap-1">
      {icons[status]}
      {displayName}
    </Badge>
  );
}

export function StandardEvalDetail({ evalId, hook }: StandardEvalDetailProps) {
  const navigate = useNavigate();
  const {
    evalData,
    runs,
    loading,
    runningEval,
    error,
    setError,
    handleRun,
    handleCancel,
    fetchData,
  } = hook;

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [copyOptDialogOpen, setCopyOptDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!evalData) {
    return (
      <div className="container max-w-6xl py-8">
        <Alert variant="destructive">
          <AlertDescription>Evaluation not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Calculate variant results from latest run
  const latestRun = runs.find((r) => r.status === 'completed');
  const variantResults = getVariantResults(evalData.variants || [], runs);

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="container max-w-6xl py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/evals')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{evalData.name}</h1>
          {evalData.description && (
            <p className="text-muted-foreground">{evalData.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCopyOptDialogOpen(true)}>
            <Sparkles className="h-4 w-4 mr-2" />
            Optimize
          </Button>
          <Button variant="outline" onClick={() => setScheduleDialogOpen(true)}>
            <Calendar className="h-4 w-4 mr-2" />
            Schedule
          </Button>
          <Button
            onClick={handleRun}
            disabled={runningEval || (evalData.variants?.length || 0) === 0}
          >
            {runningEval ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run Now
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Latest Results - Variant Comparison */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Latest Results</h2>
        {variantResults.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <p>No results yet.</p>
              <p className="text-sm mt-1">Run the evaluation to see results.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {variantResults.map((result) => (
              <Card
                key={result.name}
                className={
                  result.isBest
                    ? 'border-green-500 dark:border-green-400'
                    : ''
                }
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{result.name}</CardTitle>
                    {result.isBest && (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Best
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {result.model}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Pass Rate
                      </span>
                      <span
                        className={`text-lg font-bold ${
                          result.passRate >= 0.8
                            ? 'text-green-600'
                            : result.passRate >= 0.5
                              ? 'text-amber-600'
                              : 'text-red-600'
                        }`}
                      >
                        {Math.round(result.passRate * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Avg Score
                      </span>
                      <span className="font-mono">
                        {result.avgScore?.toFixed(2) ?? 'N/A'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() =>
                        result.runId &&
                        navigate(`/evals/${evalId}/runs/${result.runId}`)
                      }
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Configuration Section */}
      <Collapsible open={configOpen} onOpenChange={setConfigOpen}>
        <div className="flex items-center justify-between mb-4">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 p-0">
              <h2 className="text-lg font-semibold">Configuration</h2>
              {configOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditDialogOpen(true)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit Variants
          </Button>
        </div>
        <CollapsibleContent>
          <Card className="mb-6">
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Agent</p>
                  <p className="font-medium">
                    {evalData.agent?.split('/').pop()?.replace('.md', '') ||
                      'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Judge</p>
                  <p className="font-medium">
                    {evalData.judge?.agent?.split('/').pop()?.replace('.md', '') ||
                      (evalData.eval_functions?.length
                        ? evalData.eval_functions.map(f => f.name || f.function).join(', ')
                        : 'Not set')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pass Threshold</p>
                  <p className="font-medium">
                    {evalData.judge?.pass_threshold ?? 0.7}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Scenarios</p>
                  <p className="font-medium">
                    {evalData.inputs?.scenarios?.length ?? 0} test cases
                  </p>
                </div>
              </div>

              {/* Variants */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">Variants</p>
                <div className="flex flex-wrap gap-2">
                  {evalData.variants?.map((v, idx) => (
                    <Badge key={idx} variant="outline">
                      {v.name}:{' '}
                      {typeof v.model === 'string' ? v.model : v.model?.name}
                    </Badge>
                  )) || (
                    <span className="text-muted-foreground text-sm">
                      No variants configured
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* Run History */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Run History</h2>
        {runs.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <p>No runs yet.</p>
              <p className="text-sm mt-1">
                Click "Run Now" to start your first evaluation.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.slice(0, 10).map((run) => (
                  <TableRow key={run.id}>
                    <TableCell>
                      {run.started_at
                        ? formatRelativeTime(run.started_at)
                        : 'Pending'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={run.status} />
                    </TableCell>
                    <TableCell>
                      {run.completed_trials} / {run.total_trials} trials
                    </TableCell>
                    <TableCell>
                      {run.duration_ms
                        ? `${(run.duration_ms / 1000).toFixed(1)}s`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {run.status === 'running' ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleCancel(run.id)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Cancel Run</TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  navigate(`/evals/${evalId}/runs/${run.id}`)
                                }
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View Details</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>

      {/* Dialogs */}
      <CreateScheduleDialog
        evalId={evalId}
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onSuccess={() => {
          setScheduleDialogOpen(false);
        }}
      />

      <CopyAsOptimizableDialog
        evalId={evalId}
        evalName={evalData.name}
        evalData={evalData}
        open={copyOptDialogOpen}
        onClose={() => setCopyOptDialogOpen(false)}
        onSuccess={(newEvalId) => {
          setCopyOptDialogOpen(false);
          navigate(`/evals/${newEvalId}`);
        }}
      />

      <EvalWizard
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          fetchData();
        }}
        editMode
        evalId={evalId}
        initialData={evalData}
      />
    </div>
  );
}

// Helper to compute variant results from runs
interface VariantResult {
  name: string;
  model: string;
  passRate: number;
  avgScore: number | null;
  runId: string | null;
  isBest: boolean;
}

function getVariantResults(
  variants: Variant[],
  runs: EvalRun[]
): VariantResult[] {
  // For now, compute from the latest completed run
  // In a real implementation, this would aggregate across multiple runs
  const completedRuns = runs.filter((r) => r.status === 'completed');
  if (completedRuns.length === 0 || variants.length === 0) {
    return [];
  }

  // Group runs by variant (if variant info is available)
  const results: VariantResult[] = variants.map((variant, idx) => {
    const variantRuns = completedRuns.filter(
      (r) => r.variant_name === variant.name || r.variant_id === String(idx)
    );

    // Use the latest run for this variant, or fall back to any run
    const latestRun = variantRuns[0] || completedRuns[idx] || completedRuns[0];

    const passRate =
      latestRun.total_trials > 0
        ? (latestRun.completed_trials - (latestRun.failed_trials || 0)) /
          latestRun.total_trials
        : 0;

    return {
      name: variant.name,
      model:
        typeof variant.model === 'string'
          ? variant.model.split('/').pop() || variant.model
          : variant.model?.name || 'Unknown',
      passRate,
      avgScore: null, // Would need to compute from trials
      runId: latestRun.id,
      isBest: false,
    };
  });

  // Mark best variant
  if (results.length > 0) {
    const bestIdx = results.reduce(
      (best, curr, idx) => (curr.passRate > results[best].passRate ? idx : best),
      0
    );
    results[bestIdx].isBest = true;
  }

  return results;
}

export default StandardEvalDetail;
